import React from 'react';
import { getMediaUrl } from '../../services/api';

function LegacyTemplate({
  settings,
  performer,
  credits,
  skills,
  media,
  links,
  basicUser,
  isOwnProfile,
  onNavigateEdit,
  activeSection,
  setActiveSection,
  activeCreditsTab,
  setActiveCreditsTab,
  hasApplauded,
  applauseCount,
  onApplause,
  designPanel,
  inlinePopover,
}) {
  const layoutMode = settings?.layout_mode || 'scroll';
  const heroAlignment = settings?.hero_alignment || 'center';
  const headshotShape = settings?.headshot_shape || 'circle';
  const theme = settings?.theme || 'bold';

  // Custom theme styles
  const customThemeStyles =
    theme === 'custom'
      ? {
          '--profile-primary': settings?.primary_color || '#D8A45D',
          '--profile-secondary': settings?.secondary_color || '#F9EFE6',
          '--profile-accent': settings?.accent_color || '#222222',
          '--profile-bg': settings?.background_color || '#FAFAFA',
          '--profile-text': settings?.text_color || '#222222',
          '--profile-font-heading': settings?.heading_font || 'Inter',
          '--profile-font-body': settings?.body_font || 'Inter',
        }
      : {};

  // Profile display data
  const displayName =
    performer?.display_name || basicUser?.full_name || basicUser?.username || 'Unknown';
  const tagline = performer?.tagline || performer?.job_title || basicUser?.industry || '';
  const bio = performer?.bio || basicUser?.bio || '';

  // Get credit categories
  const creditCategories = credits ? Object.keys(credits) : [];

  // Get skill categories
  const skillCategories = skills ? Object.keys(skills) : [];

  const renderCreditsTable = (creditsList) => {
    if (!creditsList || creditsList.length === 0) {
      return <p style={{ color: 'var(--profile-text-secondary)' }}>No credits added yet.</p>;
    }

    return (
      <table className="profile-credits-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Production</th>
            <th>Company/Theatre</th>
            <th>Director</th>
            <th>Year</th>
          </tr>
        </thead>
        <tbody>
          {creditsList.map((credit, index) => (
            <tr key={index}>
              <td>{credit.role}</td>
              <td>{credit.production}</td>
              <td>{credit.company || '-'}</td>
              <td>{credit.director || '-'}</td>
              <td>{credit.year || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div
      className="profile-page"
      data-profile-theme={theme}
      data-layout-mode={layoutMode}
      data-hero-alignment={heroAlignment}
      style={customThemeStyles}
    >
      {/* Edit Button for own profile */}
      {isOwnProfile && (
        <div
          style={{
            position: 'fixed',
            top: '80px',
            right: '20px',
            zIndex: 1001,
          }}
        >
          <button
            onClick={onNavigateEdit}
            style={{
              padding: '12px 24px',
              background: 'var(--profile-primary)',
              color: 'var(--profile-bg)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              boxShadow: '0 4px 12px var(--profile-shadow)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit Showcase
          </button>
        </div>
      )}

      {/* HERO / HEADER SECTION */}
      <section className="profile-hero profile-section">
        <div className="profile-hero-spotlight"></div>

        <div className="profile-hero-content">
          {basicUser?.profile_image ? (
            <img
              src={basicUser.profile_image}
              alt={displayName}
              className={`profile-headshot ${headshotShape === 'square' ? 'square' : ''}`}
            />
          ) : (
            <div
              className={`profile-headshot ${headshotShape === 'square' ? 'square' : ''}`}
              style={{
                background:
                  'linear-gradient(135deg, var(--profile-secondary) 0%, var(--profile-primary) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--profile-bg)',
                fontSize: '64px',
                fontWeight: '800',
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}

          <div className="profile-hero-text">
            <h1 className="profile-name">{displayName}</h1>
            {tagline && <div className="profile-tagline">{tagline}</div>}

            {/* Contact Links */}
            {links && links.length > 0 && (
              <div className="profile-contact-buttons">
                {links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    className="profile-contact-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}

            {/* Performer Stats */}
            {performer &&
              (performer.union_status || performer.representation || performer.height) && (
                <div
                  style={{
                    marginTop: '24px',
                    display: 'flex',
                    gap: '24px',
                    flexWrap: 'wrap',
                    justifyContent: heroAlignment === 'center' ? 'center' : 'flex-start',
                    fontSize: '14px',
                    color: 'var(--profile-text-secondary)',
                  }}
                >
                  {performer.union_status && <span>{performer.union_status}</span>}
                  {performer.height && <span>{performer.height}</span>}
                  {performer.age_range && <span>Age Range: {performer.age_range}</span>}
                </div>
              )}
          </div>
        </div>
      </section>

      {/* Section Navigation for Multi-Page Mode */}
      {layoutMode === 'multipage' && (
        <nav className="profile-section-nav">
          <div className="profile-section-nav-container">
            <button
              className={`profile-nav-item ${activeSection === 'about' ? 'active' : ''}`}
              onClick={() => setActiveSection('about')}
            >
              About
            </button>
            {creditCategories.length > 0 && (
              <button
                className={`profile-nav-item ${activeSection === 'credits' ? 'active' : ''}`}
                onClick={() => setActiveSection('credits')}
              >
                Credits
              </button>
            )}
            {media && media.length > 0 && (
              <button
                className={`profile-nav-item ${activeSection === 'media' ? 'active' : ''}`}
                onClick={() => setActiveSection('media')}
              >
                Media
              </button>
            )}
            {skillCategories.length > 0 && (
              <button
                className={`profile-nav-item ${activeSection === 'skills' ? 'active' : ''}`}
                onClick={() => setActiveSection('skills')}
              >
                Skills
              </button>
            )}
          </div>
        </nav>
      )}

      <div className="profile-container">
        {/* BIO / ABOUT SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'about') && bio && (
          <section className="profile-section section-content">
            <h2 className="profile-section-title">About</h2>
            <div className="profile-bio">
              {bio.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {/* Representation info */}
            {performer?.representation && (
              <div style={{ marginTop: '24px', color: 'var(--profile-text-secondary)' }}>
                <strong>Representation:</strong> {performer.representation}
              </div>
            )}
          </section>
        )}

        {/* RESUME / CREDITS SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'credits') &&
          creditCategories.length > 0 && (
            <section className="profile-section section-content">
              <h2 className="profile-section-title">Credits</h2>

              <div className="profile-credits-tabs">
                {creditCategories.map((category) => (
                  <button
                    key={category}
                    className={`profile-credits-tab ${activeCreditsTab === category ? 'active' : ''}`}
                    onClick={() => setActiveCreditsTab(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {activeCreditsTab && renderCreditsTable(credits[activeCreditsTab])}
            </section>
          )}

        {/* MEDIA GALLERY SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'media') && media && media.length > 0 && (
          <section className="profile-section section-content">
            <h2 className="profile-section-title">Media</h2>

            <div className="profile-gallery">
              {media.map((item, index) => (
                <div key={index} className="profile-gallery-item">
                  {item.media_type === 'video' && item.embed_url ? (
                    <iframe
                      src={item.embed_url}
                      title={item.title || 'Video'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : item.media_type === 'image' && item.url ? (
                    <img src={getMediaUrl(item.url)} alt={item.title || 'Image'} />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        background:
                          'linear-gradient(135deg, var(--profile-secondary) 20%, var(--profile-primary) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--profile-bg)',
                        fontSize: '48px',
                      }}
                    >
                      {item.media_type === 'video' ? '🎬' : '📷'}
                    </div>
                  )}
                  {item.title && <div className="profile-gallery-item-overlay">{item.title}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SKILLS SECTION */}
        {(layoutMode === 'scroll' || activeSection === 'skills') && skillCategories.length > 0 && (
          <section className="profile-section section-content">
            <h2 className="profile-section-title">Special Skills</h2>

            <div className="profile-custom-block">
              {skillCategories.map((category) => (
                <div key={category} style={{ marginBottom: '24px' }}>
                  <h3
                    style={{
                      fontFamily: 'var(--profile-font-heading)',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--profile-primary)',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}
                  >
                    {category}
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {skills[category].map((skill, index) => (
                      <span
                        key={index}
                        style={{
                          padding: '8px 16px',
                          background: 'var(--profile-surface)',
                          border: '1px solid var(--profile-border)',
                          borderRadius: '20px',
                          fontSize: '14px',
                          color: 'var(--profile-text)',
                        }}
                      >
                        {skill.skill}
                        {skill.proficiency && (
                          <span
                            style={{ color: 'var(--profile-text-secondary)', marginLeft: '6px' }}
                          >
                            ({skill.proficiency})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Empty state for own profile */}
        {isOwnProfile &&
          !bio &&
          creditCategories.length === 0 &&
          (!media || media.length === 0) && (
            <section className="profile-section section-content" style={{ textAlign: 'center' }}>
              <div
                style={{
                  padding: '60px 20px',
                  background: 'var(--profile-surface)',
                  borderRadius: '12px',
                  border: '2px dashed var(--profile-border)',
                }}
              >
                <h3 style={{ color: 'var(--profile-text)', marginBottom: '16px' }}>
                  Your Showcase is Empty
                </h3>
                <p style={{ color: 'var(--profile-text-secondary)', marginBottom: '24px' }}>
                  Add your bio, credits, skills, and media to create your professional portfolio.
                </p>
                <button
                  onClick={onNavigateEdit}
                  style={{
                    padding: '14px 32px',
                    background: 'var(--profile-primary)',
                    color: 'var(--profile-bg)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '16px',
                  }}
                >
                  Start Building Your Showcase
                </button>
              </div>
            </section>
          )}
      </div>

      {/* APPLAUSE BUTTON */}
      {!isOwnProfile && (
        <div className="profile-applause">
          <button
            className={`profile-applause-btn ${hasApplauded ? 'applauded' : ''}`}
            onClick={onApplause}
            title="Applaud this performance!"
          >
            👏
            {applauseCount > 0 && <span className="profile-applause-count">{applauseCount}</span>}
          </button>
        </div>
      )}

      {designPanel}
      {inlinePopover}
    </div>
  );
}

export default LegacyTemplate;
